/**
 * The write authority for profile and user identity fields.
 *
 * Every surface that WRITES one of these fields must validate through this
 * module: the api service schemas (`api/src/services/profile-update`,
 * `api/src/services/user`), the Town Square inline editors and the legacy
 * client editor. The limits below are the one number per field — the AI prompt
 * that generates a bio (`bioPromptForMaxChars`) and the editor character
 * counters read them too, so a generated value can never exceed what a save
 * accepts.
 *
 * These caps gate NEW writes only. Stored values that predate a cap still
 * render in full, and re-submitting one unchanged must not fail a save that
 * touches other fields — see `isStoredEcho`. `shared/normalize-profile-bio.ts`
 * remains the only read-side normalizer, and nothing here duplicates it.
 */

import { z } from 'zod';

// 280, not 150: the onboarding BIO step wrote 20–280-character bios for months
// (its AI draft was generated to that target), so a tighter cap would force every
// recently onboarded artist to halve a bio they never chose the length of. The
// profile page renders the bio unclamped; the surfaces that cannot afford the
// height truncate at render.
export const BIO_MAX_LENGTH = 280;
export const DISPLAY_NAME_MAX_LENGTH = 100;
export const ARTIST_STATEMENT_MAX_LENGTH = 5000;
export const STORY_MAX_LENGTH = 2000;
export const LOCATION_MAX_LENGTH = 100;
export const LOCATION_CODE_MAX_LENGTH = 10;
export const SLUG_MIN_LENGTH = 3;
export const SLUG_MAX_LENGTH = 100;
export const MAX_PROFILE_CATEGORIES = 6;

export const FIRST_NAME_SCHEMA = z.string().optional().nullable();
export const LAST_NAME_SCHEMA = z.string().optional().nullable();
export const EMAIL_SCHEMA = z.string().email('Please enter a valid email address.');

/**
 * True when `value` breaks the cap but is byte-identical to what is stored.
 *
 * A cap gates NEW values only. Rows written before a cap existed can already
 * break it (a scraped import, an onboarding step with a larger configured
 * limit), and an editor that submits its whole form echoes the stored value
 * back — so without this exemption the first save on such a profile fails on a
 * field the user never touched, and nothing on that profile can be edited
 * again. An echo is not a new value: writers drop it from the update and the
 * editors let it validate. Any other over-limit value is still rejected.
 */
export function isStoredEcho(value: unknown, storedValue: string | null | undefined, maxLength: number): boolean {
  if (typeof value !== 'string') return false;
  if (value.length <= maxLength) return false;
  return value === storedValue;
}

/** Cap check for a single field, tolerating the stored value (see `isStoredEcho`). */
export function isWithinCap(value: unknown, maxLength: number, storedValue?: string | null): boolean {
  if (typeof value !== 'string') return true;
  if (value.length <= maxLength) return true;
  return isStoredEcho(value, storedValue, maxLength);
}

/**
 * A capped optional text field. Pass `storedValue` — the value already
 * persisted for the row being edited — to exempt an unchanged over-limit value;
 * omit it for the strict cap.
 */
function cappedString(maxLength: number, message: string, storedValue?: string | null) {
  return z
    .string()
    .refine((value) => isWithinCap(value, maxLength, storedValue), message)
    .optional()
    .nullable();
}

// User validation schemas
export const storySchema = (storedValue?: string | null) =>
  cappedString(STORY_MAX_LENGTH, `Story must be less than ${STORY_MAX_LENGTH} characters`, storedValue);
export const STORY_SCHEMA = storySchema();

// Profile validation schemas
export const bioSchema = (storedValue?: string | null) =>
  cappedString(BIO_MAX_LENGTH, `Bio must be less than ${BIO_MAX_LENGTH} characters`, storedValue);
export const BIO_SCHEMA = bioSchema();
export const DISPLAY_NAME_SCHEMA = z
  .string()
  .max(DISPLAY_NAME_MAX_LENGTH, `Display name must be less than ${DISPLAY_NAME_MAX_LENGTH} characters`)
  .optional()
  .nullable();
export const ARTIST_STATEMENT_SCHEMA = z
  .string()
  .max(ARTIST_STATEMENT_MAX_LENGTH, `Artist statement must be less than ${ARTIST_STATEMENT_MAX_LENGTH} characters`)
  .optional()
  .nullable();
export const COUNTRY_SCHEMA = z
  .string()
  .max(LOCATION_MAX_LENGTH, `Country must be less than ${LOCATION_MAX_LENGTH} characters`)
  .optional()
  .nullable();
export const REGION_SCHEMA = z
  .string()
  .max(LOCATION_MAX_LENGTH, `Region must be less than ${LOCATION_MAX_LENGTH} characters`)
  .optional()
  .nullable();
export const CITY_SCHEMA = z
  .string()
  .max(LOCATION_MAX_LENGTH, `City must be less than ${LOCATION_MAX_LENGTH} characters`)
  .optional()
  .nullable();

// New location field schemas
export const COUNTRY_CODE_SCHEMA = z
  .string()
  .max(LOCATION_CODE_MAX_LENGTH, `Country code must be less than ${LOCATION_CODE_MAX_LENGTH} characters`)
  .optional()
  .nullable();
export const REGION_CODE_SCHEMA = z
  .string()
  .max(LOCATION_CODE_MAX_LENGTH, `Region code must be less than ${LOCATION_CODE_MAX_LENGTH} characters`)
  .optional()
  .nullable();
export const LATITUDE_SCHEMA = z.number().min(-90).max(90).optional().nullable();
export const LONGITUDE_SCHEMA = z.number().min(-180).max(180).optional().nullable();
export const TIMEZONE_SCHEMA = z
  .string()
  .max(LOCATION_MAX_LENGTH, `Timezone must be less than ${LOCATION_MAX_LENGTH} characters`)
  .optional()
  .nullable();

/**
 * A slug is a public URL segment, so the format rules have to match the api's
 * `validateSlug` exactly — messages included — or the two surfaces disagree on
 * the same value. Reserved words are NOT checked here: that list is a
 * server-side concern (`api/src/const/profile.ts`) the browser must not decide.
 */
export const SLUG_SCHEMA = z
  .string()
  .min(SLUG_MIN_LENGTH, `Slug must be at least ${SLUG_MIN_LENGTH} characters`)
  .max(SLUG_MAX_LENGTH, `Slug must be less than ${SLUG_MAX_LENGTH} characters`)
  .regex(/^[a-z0-9-]+$/, 'Slug can only contain lowercase letters, numbers, and hyphens')
  .refine(
    (slug) => !slug.includes('--') && !slug.startsWith('-') && !slug.endsWith('-'),
    'Slug cannot have consecutive hyphens or start/end with hyphens'
  )
  .optional()
  .nullable();

// Image URL validation schemas
export const AVATAR_URL_SCHEMA = z.string().url('Please enter a valid URL').optional().nullable();
export const BANNER_URL_SCHEMA = z.string().url('Please enter a valid URL').optional().nullable();

// Category validation schemas
export const CATEGORY_SCHEMA = z.object({
  categoryId: z.string(),
  isPrimary: z.boolean().optional(),
});

export type ProfileCategoryInput = z.infer<typeof CATEGORY_SCHEMA>;

function hasSinglePrimary(categories: ProfileCategoryInput[]): boolean {
  // An empty array clears every category, so there is no primary to name.
  if (categories.length === 0) return true;
  return categories.filter((category) => category.isPrimary === true).length === 1;
}

/**
 * A category selection is capped and must name exactly one primary — the
 * primary drives discovery ranking and the public category label, so zero or
 * several of them leaves the profile unrepresentable.
 */
export const CATEGORIES_SCHEMA = z
  .array(CATEGORY_SCHEMA)
  .max(MAX_PROFILE_CATEGORIES, `Select at most ${MAX_PROFILE_CATEGORIES} categories`)
  .refine(hasSinglePrimary, 'Exactly one category must be marked as primary');

/** Throws the first rule violation as a plain Error, for non-zod call sites. */
export function assertProfileCategories(categories: unknown): void {
  const result = CATEGORIES_SCHEMA.safeParse(categories);
  if (result.success) return;
  throw new Error(result.error.errors[0]?.message ?? 'Invalid categories');
}

/**
 * Section visibility toggles (`showMockups`, `showArtworkSpecs`,
 * `showActivity`) are read as `!== false` everywhere, so an explicit `null`
 * would silently erase a deliberate "off" back into the on-by-default state.
 * The field stays OPTIONAL — `updateProfile` is a partial update and a save
 * that does not touch a toggle must keep working — but a value that IS present
 * has to be a real boolean.
 */
export const VISIBILITY_TOGGLE_SCHEMA = z
  .boolean({ invalid_type_error: 'Visibility toggles must be true or false' })
  .optional();

// Social link validation schemas
export const SOCIAL_LINK_TYPE_SCHEMA = z.string().min(1, 'Type is required');
export const SOCIAL_LINK_URL_SCHEMA = z
  .string()
  .min(1, 'URL is required')
  .refine((url) => {
    try {
      const urlObj = new URL(url);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  }, 'Please enter a valid URL with http:// or https://')
  .refine((url) => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.length > 0 && urlObj.hostname.includes('.');
    } catch {
      return false;
    }
  }, 'Please enter a complete URL with a valid domain');
export const SOCIAL_LINK_TITLE_SCHEMA = z.string().optional();
export const SOCIAL_LINK_SCHEMA = z.object({
  type: SOCIAL_LINK_TYPE_SCHEMA,
  url: SOCIAL_LINK_URL_SCHEMA,
  title: SOCIAL_LINK_TITLE_SCHEMA,
});
